import { cn, GlassCard, GlassCardContent } from '@merritrac/shared/components';
import { MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useTheme } from '../lib/theme.js';

import type { ReactElement } from 'react';

interface ServiceOrderMapProps {
  address: string;
  className?: string;
}

const TOKEN: string = (import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined) ?? '';

export function ServiceOrderMap({ address, className }: ServiceOrderMapProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const { theme } = useTheme();
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [geocodeError, setGeocodeError] = useState(false);

  // Geocode the address
  useEffect((): (() => void) | undefined => {
    if (address === '' || TOKEN === '') {
      setGeocodeError(true);
      return undefined;
    }

    let cancelled = false;
    const encoded = encodeURIComponent(address);

    void fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${TOKEN}&limit=1`,
    )
      .then(
        (res): Promise<{ features?: { center: [number, number] }[] }> =>
          res.json() as Promise<{ features?: { center: [number, number] }[] }>,
      )
      .then((data: { features?: { center: [number, number] }[] }): void => {
        if (cancelled) {
          return;
        }
        const feature = data.features?.[0];
        if (feature !== undefined) {
          setCoords(feature.center);
          setGeocodeError(false);
        } else {
          setGeocodeError(true);
        }
      })
      .catch((): void => {
        if (!cancelled) {
          setGeocodeError(true);
        }
      });

    return (): void => {
      cancelled = true;
    };
  }, [address]);

  // Initialize map once we have coordinates
  useEffect((): (() => void) | undefined => {
    if (coords === null || containerRef.current === null || TOKEN === '') {
      return undefined;
    }

    // Clean up previous map instance
    if (mapInstanceRef.current !== null) {
      (mapInstanceRef.current as { remove: () => void }).remove();
      mapInstanceRef.current = null;
    }

    let cancelled = false;

    void import('mapbox-gl').then((mapboxglModule): void => {
      if (cancelled || containerRef.current === null) {
        return;
      }

      const link = document.getElementById('mapbox-gl-css') ?? document.createElement('link');
      if (link.id !== 'mapbox-gl-css') {
        link.id = 'mapbox-gl-css';
        (link as HTMLLinkElement).rel = 'stylesheet';
        (link as HTMLLinkElement).href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
        document.head.appendChild(link);
      }

      const mapboxgl = mapboxglModule.default;
      mapboxgl.accessToken = TOKEN;

      const map = new mapboxgl.Map({
        container: containerRef.current,
        style:
          theme === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
        center: coords,
        zoom: 15,
        attributionControl: false,
      });

      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

      map.on('load', (): void => {
        // Add marker
        const el = document.createElement('div');
        el.className = 'service-order-marker';
        el.style.cssText = `
          width: 32px; height: 32px; border-radius: 50%;
          background: var(--color-primary, #3b82f6);
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        `;
        el.innerHTML =
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';

        new mapboxgl.Marker({ element: el }).setLngLat(coords).addTo(map);
      });

      mapInstanceRef.current = map;
    });

    return (): void => {
      cancelled = true;
      if (mapInstanceRef.current !== null) {
        (mapInstanceRef.current as { remove: () => void }).remove();
        mapInstanceRef.current = null;
      }
    };
  }, [coords, theme]);

  if (geocodeError || address === '' || TOKEN === '') {
    return (
      <GlassCard className={cn('h-[250px]', className)}>
        <GlassCardContent className="h-full flex flex-col items-center justify-center gap-2 text-text-tertiary">
          <MapPin size={32} />
          <p className="text-sm">No address available</p>
          {address !== '' && (
            <p className="text-xs text-text-tertiary/70 text-center max-w-[200px]">{address}</p>
          )}
        </GlassCardContent>
      </GlassCard>
    );
  }

  return (
    <GlassCard className={cn('overflow-hidden', className)}>
      <div ref={containerRef} className="h-[250px] w-full" />
      <div className="px-4 py-2 flex items-center gap-1.5 text-xs text-text-secondary border-t border-[var(--glass-border-subtle)]">
        <MapPin size={12} className="shrink-0 text-primary" />
        <span className="truncate">{address}</span>
      </div>
    </GlassCard>
  );
}
